import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import params from '../config/Params';
import { getItemFromLocalStorage, setWithExpiry } from '../utils/storageUtils';
import { toast } from 'react-toastify';
import { Helmet } from 'react-helmet';
import ImageCropper from '../components/ImageCropper';

// ✅ Regex Definitions
const regex = {
  name: /^[A-Za-z\s'-]{2,50}$/, // allows letters, spaces, apostrophe, dash
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  phone: /^\d{10}$/,
  gst: /^[0-9A-Z]{15}$/, // strict GSTIN format
  company: /^(?=.*[A-Za-z])[A-Za-z0-9\s&.,()-]+$/, // must have letters, can have numbers
  brand: /^(?=.*[A-Za-z])[A-Za-z0-9\s&.,()-]{2,50}$/, // 2–50 chars, must have letters
  bio: /^[A-Za-z0-9\s.,%&()+\-–—]*$/, // letters, numbers, punctuation, % & + -
};

function MyProfile() {
  const [sourceId, setSourceId] = useState('');
  const [hostId, setHostId] = useState('');
  const [firstName, setFirstName] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [lastName, setLastName] = useState('');
  const [gstin, setGstin] = useState('');
  const [gstError, setGstError] = useState("");
  const [errors, setErrors] = useState({});
  const [bio, setBio] = useState('');
  const [bioError, setBioError] = useState("");

  const [email, setEmail] = useState('');
  const [number, setNumber] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [brandName, setBrandName] = useState('');
  const [imagePreview, setImagePreview] = useState('/assets/img/host_default_image.png');
  const [successMessage, setSuccessMessage] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [croppedImage, setCroppedImage] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const successMessageRef = useRef(null);

  useEffect(() => {
    if (successMessage && successMessageRef.current) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [successMessage]);

  useEffect(() => {
    const fetchProfileData = async () => {
      const hostData = getItemFromLocalStorage('hostProfile');

      let formData = new FormData();
      formData.append('token', hostData.host_access_token);

      try {
        const response = await axios({
          method: 'post',
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          url: `${params.baseURL}${params.endpoints.getProfile}`,
          data: formData
        });

        if (response.data.status === 'found') {
          const profileData = response.data.profile_data;
          setFirstName(profileData.host_name || '');
          setMiddleName(profileData.host_midname || '');
          setLastName(profileData.host_lastname || '');
          setGstin(profileData.host_gst_no || '');
          setCompanyName(profileData.host_legal_name || '');
          setBrandName(profileData.brand_name || '');
          setEmail(profileData.host_contact_email || '');
          setNumber(profileData.host_contact_number || '');
          setHostId(profileData.host_id || '');
          setSourceId(profileData.host_login_id || '');
          setBio(profileData.host_bio || '');
          setImagePreview(profileData.host_logo || '/assets/img/host_default_image.png');
        } else if (response.data.status === 'error') {
          toast.error(response.data.list[0]);
        } else {
          toast.error('Error in saving data.');
        }
      } catch (err) {
        toast.error('An error occurred while saving data.');
      }
    };

    fetchProfileData();
  }, []);

  const handleCrop = (croppedDataUrl) => {
    setCroppedImage(croppedDataUrl);
    console.log(croppedDataUrl);
    setShowCropper(false);
  };

  const base64ToBlob = (base64, mime) => {
    const byteString = atob(base64.split(',')[1]);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: mime });
  };

  const handleGstinChange = async (e) => {
    const value = e.target.value;
    setGstin(value);

    if (!regex.gst.test(value)) {
      setFormErrors((p) => ({ ...p, gstin: true }));
      return;
    }

    setFormErrors((p) => ({ ...p, gstin: false }));
    // Step 1: Client-side validation
    if (value.length < 15) {
      setErrors((prev) => ({
        ...prev,
        gstin: "GSTIN must be exactly 15 characters."
      }));
      return;
    }

    if (!regex.gst.test(value)) {
      setErrors((prev) => ({
        ...prev,
        gstin: "GSTIN must contain only uppercase letters (A-Z) and numbers (0-9)."
      }));
      return;
    }

    // Clear error if regex passes
    setErrors((prev) => ({ ...prev, gstin: null }));

    // Step 2: Server-side validation (only if regex passed and length is 15)
    if (value.length === 15) {
      let gstData = new FormData();
      gstData.append("gstin", value);

      try {
        const response = await axios({
          method: "post",
          headers: { "Content-Type": "multipart/form-data" },
          url: `${params.baseURL}${params.endpoints.verifyGst}`,
          data: gstData,
        });

        if (response.data.status === "ok") {
          setCompanyName(response.data.gst_legal_name);
        } else if (response.data.status === "error") {
          setErrors((prev) => ({ ...prev, gstin: response.data.list || "Invalid GSTIN." }));
          toast.error(response.data.list);
        } else {
          toast.error("An unknown error occurred while verifying GSTIN.");
        }
      } catch (err) {
        toast.error("Server error while verifying GSTIN.");
      }
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!firstName) errors.firstName = true;
    if (!lastName) errors.lastName = true;

    if (!email || !regex.email.test(email)) {
      errors.email = true;
    }

    if (!number || !regex.phone.test(number)) {
      errors.number = true;
    }

    return errors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    switch (name) {
      case "firstName":
        setFirstName(value);
        setFormErrors((p) => ({ ...p, firstName: !regex.name.test(value) }));
        break;
      case "middleName":
        setMiddleName(value);
        setFormErrors((p) => ({ ...p, middleName: value && !regex.name.test(value) }));
        break;
      case "lastName":
        setLastName(value);
        setFormErrors((p) => ({ ...p, lastName: !regex.name.test(value) }));
        break;
      case 'email':
        setEmail(value);
        setFormErrors((prev) => ({
          ...prev,
          email: !regex.email.test(value),
        }));
        break;
      case 'number':
        setNumber(value);
        setFormErrors((prev) => ({
          ...prev,
          number: !regex.phone.test(value),
        }));
        break;
      case "companyName":
        setCompanyName(value);
        setFormErrors((p) => ({ ...p, companyName: !regex.company.test(value) }));
        break;
      case "brandName":
        setBrandName(value);
        setFormErrors((p) => ({ ...p, brandName: !regex.brand.test(value) }));
        break;
      default:
        break;
    }
  };

  const handleBioChange = (e) => {
    const value = e.target.value;
    setBio(value);
    if (value.length < 20) {
      setBioError("Bio must be at least 20 characters long.");
    } else if (value.length > 500) {
      setBioError("Bio cannot exceed 500 characters.");
    } else if (!regex.bio.test(value)) {
      setBioError(
        "Only letters, numbers, spaces, and basic punctuation ( , . - ( ) % & + ) are allowed."
      );
    } else {
      setBioError("");
    }
  }

  const ProceedRegister = async (e) => {
    e.preventDefault();
    const errors = validateForm();
    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      toast.error("Please correct the highlighted fields.");
      return;
    }

    const hostData = getItemFromLocalStorage('hostProfile');

    let formData = new FormData();
    formData.append('token', hostData.host_access_token);
    formData.append('id', hostId);
    formData.append('host_login_source', 'OTP');
    formData.append('host_login_id', sourceId);
    formData.append('host_name', firstName);
    formData.append('host_midname', middleName);
    formData.append('host_lastname', lastName);
    formData.append('host_contact_number', number);
    formData.append('host_gst_no', gstin);
    formData.append('host_legal_name', companyName);
    formData.append('host_contact_email', email);
    formData.append('brand_name', brandName);
    formData.append('host_bio', bio);

    try {
      const response = await axios({
        method: 'post',
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        url: `${params.baseURL}${params.endpoints.updateProfile}`,
        data: formData
      });

      if (response.data.status === 'updated') {
        setWithExpiry('hostProfile', JSON.stringify(response.data.profile_data));
        toast.success('Your profile data updated successfully.');
      } else if (response.data.status === 'error') {
        toast.error(response.data.list[0]);
      } else {
        toast.error('Error in saving data.');
      }
    } catch (err) {
      toast.error('An error occurred while saving data.');
    }
  }

  const UpdateImage = async (imageDataUrl) => {
    const hostData = getItemFromLocalStorage('hostProfile');

    let formData = new FormData();
    formData.append('token', hostData.host_access_token);
    if (imageDataUrl) {
      const blob = base64ToBlob(imageDataUrl, 'image/jpg');
      formData.append('host_logo', blob);
    }
    formData.append('host_gst_no', gstin);
    formData.append('host_legal_name', companyName);
    try {
      const response = await axios({
        method: 'post',
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        url: `${params.baseURL}${params.endpoints.updateProfile}`,
        data: formData
      });

      if (response.data.status === 'updated') {
        setWithExpiry('hostProfile', JSON.stringify(response.data.profile_data));
        toast.success('Your profile photo updated successfully.');
      } else if (response.data.status === 'error') {
        toast.error(response.data.list);
      } else {
        toast.error('Error in saving data.');
      }
    } catch (err) {
      toast.error('An error occurred while saving data.');
    }
  }

  return (
    <div>
      <Helmet>
        <title>My Profile</title>
      </Helmet>
      <section className="registration-form" style={{ height: '100%', overflow: 'hidden' }}>
        <div className='row'>
          <div className='col-md-6 register-left-panel'>
          </div>
          <div className='col-md-6 offset-md-6'>
            <div className="form-body">
              <div className="row">
                <div className="form-holder">
                  <div className="form-content">
                    <div className="form-items">
                      <h4 className='register-title mb-2'>Update Profile</h4>
                      <p className='mb-5'>Keep your profile fresh and up-to-date—because first impressions matter!</p>
                      <form className="needs-validation" encType="multipart/form-data" onSubmit={ProceedRegister}>
                        <div className='row'>
                          <div className="col-md-12 mb-4">
                            <div className='form-flex'>
                              <div className='pimg form-icon'>
                                <img
                                  src='/assets/img/icons/profile_image_icon.svg'
                                  className='pimg form-icon img-fluid'
                                  alt=''
                                />
                              </div>
                              <div className="form-txt host-profile-section">
                                <label onClick={() => setShowCropper(true)}>
                                  <div>
                                    <img src={croppedImage || imagePreview} alt="Preview" className='host-profile-img' />
                                  </div>
                                </label>
                                {showCropper && (
                                  <ImageCropper
                                    onCrop={(croppedDataUrl) => {
                                      handleCrop(croppedDataUrl);
                                      UpdateImage(croppedDataUrl);
                                    }}
                                    onClose={() => setShowCropper(false)}
                                  />
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="col-md-12 mb-4">
                            <div className='form-flex form-icon-box'>
                              <div className='form-icon'>
                                <img
                                  src='/assets/img/icons/legal_name_icon.svg'
                                  className='form-icon img-fluid'
                                  alt=''
                                />
                              </div>
                              <div className='form-txt'>
                                <div className="form-floating">
                                  <input className={`form-control ${formErrors.firstName ? 'is-invalid' : ''}`} type="text" id="firstName"
                                    name="firstName" value={firstName} onChange={handleChange} onKeyUp={handleChange} onKeyDown={(e) => {
                                      if (e.key === " ") e.preventDefault();
                                    }} />
                                  <label htmlFor="firstName">First Name</label>
                                  {formErrors.firstName && (
                                    <div className="invalid-feedback">
                                      Only alphabets, spaces, hyphens, and apostrophes allowed (2–50 chars).
                                    </div>
                                  )}
                                </div>
                                {/*<div className="txt-msg hcode-msg mb-0 mt-2" style={{background:'#e5e4e2',border:'1px solid #e5e4e2'}}>
                                  <p className="pid">Dear Host, your first name will be used as your preferred name until you add one yourself. The preferred name is what users see to know who their host is.</p>
                                </div>*/}
                              </div>
                            </div>
                          </div>
                          <div className="col-md-12 mb-4">
                            <div className='form-flex form-icon-box'>
                              <div className='form-icon'>
                                <img
                                  src='/assets/img/icons/legal_name_icon.svg'
                                  className='form-icon img-fluid'
                                  alt=''
                                />
                              </div>
                              <div className='form-txt'>
                                <div className="form-floating">
                                  <input className={`form-control ${formErrors.middleName ? 'is-invalid' : ''}`} type="text" id="middleName"
                                    name="middleName" value={middleName} onChange={handleChange} onKeyUp={handleChange} />
                                  <label htmlFor="middleName">Middle Name</label>
                                  {formErrors.middleName && (
                                    <div className="invalid-feedback">
                                      Only alphabets, spaces, hyphens, and apostrophes allowed (2–50 chars).
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="col-md-12 mb-4">
                            <div className='form-flex form-icon-box'>
                              <div className='form-icon'>
                                <img
                                  src='/assets/img/icons/legal_name_icon.svg'
                                  className='form-icon img-fluid'
                                  alt=''
                                />
                              </div>
                              <div className='form-txt'>
                                <div className="form-floating">
                                  <input className={`form-control ${formErrors.lastName ? 'is-invalid' : ''}`} type="text" id="lastName"
                                    name="lastName" value={lastName} onChange={handleChange} onKeyUp={handleChange} />
                                  <label htmlFor="lastName">Last Name</label>
                                  {formErrors.lastName && (
                                    <div className="invalid-feedback">
                                      Only alphabets, spaces, hyphens, and apostrophes allowed (2–50 chars).
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="col-md-12 mb-4">
                            <div className='form-flex'>
                              <div className='pvline form-icon'></div>
                              <div className='form-txt'>
                                <div className="form-floating">
                                  <textarea
                                    // className={`form-control ${error ? "is-invalid" : "is-valid"}`}
                                    className={`form-control ${bioError ? "is-invalid" : "is-valid"}`}
                                    type="text" id="bio"
                                    name="bio" cols={4} rows={5} value={bio} style={{ height: 'auto' }}
                                    onChange={handleBioChange} onKeyUp={handleBioChange}></textarea>
                                  <label htmlFor="bio">Bio</label>
                                  {bioError && (
                                    <div className="invalid-feedback">
                                      {bioError}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className='row'>
                          <div className="col-md-12 mb-4">
                            <div className='form-flex form-icon-box'>
                              <div className='form-icon'>
                                <img
                                  src='/assets/img/icons/mail_icon.svg'
                                  className='form-icon img-fluid'
                                  alt=''
                                />
                              </div>
                              <div className='form-txt'>
                                <div className="form-floating">
                                  <input className={`form-control ${formErrors.email ? 'is-invalid' : ''}`} type="email" id="email"
                                    name="email" value={email} onChange={handleChange} onKeyUp={handleChange} />
                                  <label htmlFor="email">Email ID</label>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="col-md-12 mb-4">
                            <div className='form-flex form-icon-box'>
                              <div className='form-icon'>
                                <img
                                  src='/assets/img/icons/contact_number_icon.svg'
                                  className='form-icon img-fluid'
                                  alt=''
                                />
                              </div>
                              <div className='form-txt'>
                                <div className="form-floating">
                                  <input className={`form-control ${formErrors.number ? 'is-invalid' : ''}`}
                                    type="text" id="number" name="number" value={number} onChange={handleChange} onKeyUp={handleChange} />
                                  <label htmlFor="number">Number</label>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className='row'>
                          <div className="col-md-12 mb-4">
                            <div className='form-flex form-icon-box'>
                              <div className='form-icon'>
                                <img
                                  src='/assets/img/icons/gst_icon.svg'
                                  className='form-icon img-fluid'
                                  alt=''
                                />
                              </div>
                              <div className='form-txt'>
                                <div className="form-floating">
                                  <input className={`form-control ${gstError ? "is-invalid" : ""}`} type="text" maxlength="15" id="gstin"
                                    name="gstin" value={gstin} onChange={handleGstinChange} />
                                  <label htmlFor="gstin">GSTIN (If Applicable)</label>
                                  {gstError && <div className="invalid-feedback">{gstError}</div>}
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="col-md-12 mb-4">
                            <div className='form-flex form-icon-box'>
                              <div className='form-icon'>
                                <img
                                  src='/assets/img/icons/company_icon.svg'
                                  className='form-icon img-fluid'
                                  alt=''
                                />
                              </div>
                              <div className='form-txt'>
                                <div className="form-floating">
                                  <input className={`form-control ${formErrors.companyName ? 'is-invalid' : ''}`}
                                    type="text"
                                    id="companyName"
                                    name="companyName"
                                    value={companyName}
                                    onChange={handleChange}
                                    onKeyUp={handleChange} />
                                  <label htmlFor="companyName">Company Name</label>
                                  {formErrors.companyName && (
                                    <div className="invalid-feedback">
                                      Alphabets required, numbers allowed, but not purely numeric.
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="col-md-12 mb-4">
                            <div className='form-flex form-icon-box'>
                              <div className='form-icon'>
                                <img
                                  src='/assets/img/icons/brand_name_icon.svg'
                                  className='form-icon img-fluid'
                                  alt=''
                                />
                              </div>
                              <div className='form-txt'>
                                <div className="form-floating">
                                  <input className={`form-control ${formErrors.brandName ? "is-invalid" : ""}`}
                                    type="text" id="brandName" name="brandName" value={brandName} onChange={handleChange} />
                                  <label htmlFor="brandName">Brand Name</label>
                                  {formErrors.brandName && (
                                    <div className="invalid-feedback">
                                      Brand must contain letters, 2–50 characters.
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className='row'>
                          <div className="col-md-12 mb-3">
                            <div className='form-flex form-icon-box'>
                              <div className='form-icon'>
                                <img
                                  src='/assets/img/icons/register_icon.svg'
                                  className='form-icon img-fluid'
                                  alt=''
                                />
                              </div>
                              <div className='form-txt'>
                                <div className="form-button">
                                  <button id="submit" type="submit" className="register-btn">Submit</button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className='row'>
                          <div className='col-md-12'>
                            <div className="form-check tc-box" style={{ marginLeft: '55px' }}>
                              <label className="form-check-label">
                                I ACCEPT THE <a href={'https://wirestay.com/legal/hosting-agreement'} target='_blank' rel="noreferrer">TERMS & CONDITIONS</a> AND <a href={'https://wirestay.com/legal/privacy'} target='_blank' rel="noreferrer">PRIVACY POLICY</a> TO CONTINUE AS A WIRE STAY HOST PARTNER.
                              </label>
                            </div>
                          </div>
                        </div>

                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </section>
    </div>
  );
}

export default MyProfile;
